/**
 * "Formatar" um documento existente no padrão do tipo — as decisões PURAS (sem
 * Office.js), verificadas fora do Word por `npm run check:format`. A execução
 * mora em `word/formatDocument.ts`.
 *
 * O fluxo antigo pedia ao MODELO para formatar o documento inteiro num turno: o
 * turno estourava o tempo no meio, e o que já tinha sido mexido ficava com
 * danos que ninguém relatava (QA BUG-01). Agora quase tudo é decisão por
 * código, a partir do que o próprio documento diz de cada parágrafo — o estilo
 * de título, a lista a que pertence, o marcador digitado —, e o modelo entra só
 * onde há julgamento (ver `sectionsForReview`), uma seção por vez.
 */
import { typedListMarker } from './listPlan';
const QUOTES = { Quote: 'quote_type_1', IntenseQuote: 'quote_type_2' };
// Estilos de capa/abertura no meio do corpo (a folha de rosto em texto de um
// documento de terceiro): o que fazer com eles é julgamento — ficam para a
// revisão, que pode levá-los para a capa.
const ESTILOS_DE_CAPA = new Set(['Title', 'Subtitle']);
const textoDe = (p) => p.text.replace(/\f/g, '').trim();
// Lista por ESTILO ("Com marcadores", "Com marcadores 2", "Numerada 3"): cada
// estilo é uma numeração à parte, e o número do estilo é o NÍVEL.
const ESTILO_DE_LISTA = /^List(Bullet|Number)(\d)?$/;
/**
 * Legenda de figura: estilo Legenda, ou linha curta CENTRALIZADA encostada numa
 * figura. Vira corpo justificado e a legenda sai do centro — por isso fica.
 */
function isCaption(paras, i) {
    const p = paras[i];
    if (p.styleBuiltIn === 'Caption')
        return true;
    if (p.alignment !== 'Centered' || textoDe(p).length > 200)
        return false;
    const vizinhaComFigura = (j) => j >= 0 && j < paras.length && paras[j].pictures > 0;
    return vizinhaComFigura(i - 1) || vizinhaComFigura(i + 1);
}
/** O papel de cada parágrafo e as listas a montar. */
export function planHouseFormatting(paras) {
    var _a;
    const roles = [];
    const groups = [];
    const porLista = new Map(); // id da lista nativa → grupo
    const marcadorDigitado = paras.map((p) => typedListMarker(p.text));
    // Numeração digitada só é lista quando vem em sequência (2+ linhas seguidas
    // de CORPO): uma linha solta "1. Introdução" é título disfarçado, e dois
    // títulos seguidos ("1. Objetivo", "1.1. Escopo") são títulos.
    const candidatoNumerado = (j) => {
        var _a;
        return j >= 0 &&
            j < paras.length &&
            ((_a = marcadorDigitado[j]) === null || _a === void 0 ? void 0 : _a.kind) === 'numbered' &&
            !paras[j].list &&
            paras[j].headingLevel === null &&
            !ESTILOS_DE_CAPA.has(paras[j].styleBuiltIn);
    };
    const emSequenciaNumerada = (i) => candidatoNumerado(i) && (candidatoNumerado(i - 1) || candidatoNumerado(i + 1));
    // Lista que se monta por VIZINHANÇA (por estilo ou com marcador digitado):
    // a última aberta, que um parágrafo de conteúdo encerra. Linha em branco no
    // meio não encerra — uma numerada recomeçaria do 1.
    let aberta = null;
    const continuar = (origem, kind) => {
        if (aberta && aberta.origem === origem && (origem === 'estilo' || groups[aberta.group].kind === kind)) {
            return aberta.group;
        }
        const g = groups.push({ kind, indices: [], levels: [], typed: origem === 'digitado' }) - 1;
        aberta = { group: g, origem };
        return g;
    };
    for (let i = 0; i < paras.length; i++) {
        const p = paras[i];
        if (p.region !== 'body') {
            roles.push({ kind: 'skip', why: 'protegido' });
            aberta = null;
            continue;
        }
        if (p.inTable) {
            roles.push({ kind: 'skip', why: 'tabela' });
            aberta = null;
            continue;
        }
        if (!textoDe(p)) {
            roles.push({ kind: 'skip', why: p.pictures ? 'figura' : 'vazio' });
            if (p.pictures)
                aberta = null;
            continue;
        }
        if (p.list) {
            const porEstilo = ESTILO_DE_LISTA.exec(p.styleBuiltIn);
            if (porEstilo) {
                // As linhas seguidas em estilos de lista são UMA lista: separadas,
                // "Com marcadores 2" virava lista à parte e a hierarquia achatava.
                const g = continuar('estilo', porEstilo[1] === 'Number' ? 'numbered' : 'bullet');
                groups[g].indices.push(i);
                groups[g].levels.push(porEstilo[2] ? Number(porEstilo[2]) - 1 : 0);
                roles.push({ kind: 'list', group: g });
                continue;
            }
            // Todos os itens da mesma lista nativa numa lista da marca só — mesmo com
            // um parágrafo de texto no meio, a numeração segue.
            let g = porLista.get(p.list.id);
            if (g === undefined) {
                g = groups.length;
                porLista.set(p.list.id, g);
                groups.push({ kind: (_a = p.list.kind) !== null && _a !== void 0 ? _a : 'bullet', indices: [], levels: [], typed: false });
            }
            groups[g].indices.push(i);
            groups[g].levels.push(p.list.level);
            roles.push({ kind: 'list', group: g });
            aberta = null;
            continue;
        }
        // Estilo de capa ANTES do nível de título: um nome de estilo como
        // "Subtítulo" não é título.
        if (p.headingLevel === 0 || ESTILOS_DE_CAPA.has(p.styleBuiltIn)) {
            aberta = null;
            roles.push({ kind: 'skip', why: 'capa-antiga' });
            continue;
        }
        if (p.headingLevel !== null && p.headingLevel >= 1) {
            aberta = null;
            roles.push({ kind: 'style', ref: `heading_${Math.min(p.headingLevel, 4)}` });
            continue;
        }
        if (QUOTES[p.styleBuiltIn]) {
            aberta = null;
            roles.push({ kind: 'style', ref: QUOTES[p.styleBuiltIn] });
            continue;
        }
        if (isCaption(paras, i)) {
            aberta = null;
            roles.push({ kind: 'skip', why: 'legenda' });
            continue;
        }
        const m = marcadorDigitado[i];
        if ((m === null || m === void 0 ? void 0 : m.kind) === 'bullet' || emSequenciaNumerada(i)) {
            // Lista antiga (marcador digitado): as linhas seguidas do mesmo tipo são
            // uma lista.
            const g = continuar('digitado', m.kind);
            groups[g].indices.push(i);
            groups[g].levels.push(0);
            roles.push({ kind: 'list', group: g });
            continue;
        }
        aberta = null;
        roles.push({ kind: 'style', ref: 'body_paragraph' });
    }
    return { roles, groups };
}
const MESES = 'janeiro|fevereiro|março|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro';
const DATA_EM_TEXTO = new RegExp(`\\b\\d{1,2}\\/\\d{1,2}\\/\\d{2,4}\\b|\\b\\d{1,2} de (?:${MESES}) de \\d{4}\\b|` +
    `\\b(?:${MESES})(?: de |\\/)\\d{4}\\b|\\b\\d{4}-\\d{2}-\\d{2}\\b`, 'i');
/** A data que um texto escreve ("15/03/2026", "março de 2026"), como está escrita. */
export function findDateMention(text) {
    var _a, _b;
    return (_b = (_a = DATA_EM_TEXTO.exec(text || '')) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
}
/**
 * Palavras de folha de rosto que não são informação: rótulos ("Cliente:",
 * "Elaborado por"), a marca e conectivos. Tirá-las é o que deixa "Cliente XYZ"
 * zerar quando a capa diz "XYZ".
 */
const SEM_INFORMACAO = new Set([
    'cliente', 'clientes', 'para', 'preparado', 'preparada', 'elaborado', 'elaborada',
    'por', 'data', 'versão', 'versao', 'rev', 'revisão', 'revisao', 'documento',
    'título', 'titulo', 'confidencial', 'norven', 'proposta', 'técnica', 'tecnica',
    'de', 'do', 'da', 'dos', 'das', 'em', 'no', 'na', 'ao', 'aos',
]);
/**
 * O que a folha de rosto antiga diz e a capa ainda não: o que sobra de cada
 * linha depois de tirar título, cliente, data e tipo da capa, as datas (a data
 * tem rede própria, que a leva para a capa), os rótulos e a pontuação. Vazio =
 * dá para apagar sem perder nada.
 *
 * Medido com o modelo real: numa de duas rodadas ele levou título e data para a
 * capa, esqueceu o cliente ("Cliente XYZ S.A.") e apagou a folha de rosto — o
 * nome do cliente sumiu do documento. Quem decide O QUE é cliente continua
 * sendo o modelo; isto só impede que a informação se perca no caminho.
 */
export function coverSheetLeftovers(lines, cover) {
    const norm = (s) => (s || '').normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim();
    const valores = [cover.title, cover.client, cover.date, cover.documentType]
        .map(norm)
        .filter((v) => v.length >= 2)
        .sort((a, b) => b.length - a.length); // o mais longo primeiro: "XYZ S.A." antes de "XYZ"
    const datas = new RegExp(DATA_EM_TEXTO.source, 'gi');
    const sobras = [];
    for (const linha of lines) {
        let resto = norm(linha);
        for (const v of valores)
            resto = resto.split(v).join(' ');
        resto = resto.replace(datas, ' ');
        const palavras = resto
            .split(/[^0-9a-zà-öø-ÿ]+/i)
            .filter((w) => w.length >= 2 && !SEM_INFORMACAO.has(w) && !/^\d{4}$/.test(w));
        if (palavras.length)
            sobras.push(palavras.join(' '));
    }
    return sobras;
}
// "1. Objetivo", "1.1 Escopo", "3.1.1. Detalhe".
const NUMERADO = /^\s*\d+(?:\.\d+)*\.?\s+\S/;
const TERMINA_FRASE = /[.;:!?…]["'”’)\]]*\s*$/;
/**
 * Seções que PRECISAM do modelo e o que há em cada uma. Cada ponto é algo que o
 * código não decide sozinho: numerar um título (uma carta de encaminhamento não
 * leva número), promover a título uma linha em negrito, o que fazer com a folha
 * de rosto antiga em texto. Seção sem ponto nenhum não gasta um turno.
 */
export function sectionsForReview(paras, plan) {
    var _a;
    const secoes = [];
    let atual = null;
    const abrir = (title, headingIndex, start) => {
        if (atual)
            atual.end = start - 1;
        atual = { title, headingIndex, start, end: paras.length - 1, issues: [] };
        secoes.push(atual);
    };
    for (let i = 0; i < paras.length; i++) {
        const role = plan.roles[i];
        const p = paras[i];
        if (role.kind === 'skip' && role.why === 'protegido')
            continue;
        const texto = textoDe(p);
        if (role.kind === 'style' && role.ref === 'heading_1')
            abrir(texto, i, i);
        else if (!atual)
            abrir('', null, i);
        const secao = atual;
        if (role.kind === 'style' && /^heading_[1-3]$/.test(role.ref) && !NUMERADO.test(texto)) {
            secao.issues.push({ index: i, kind: 'titulo-sem-numero', text: texto });
        }
        else if (role.kind === 'style' &&
            role.ref === 'body_paragraph' &&
            p.allBold &&
            texto.length <= 90 &&
            !TERMINA_FRASE.test(texto)) {
            secao.issues.push({ index: i, kind: 'titulo-disfarcado', text: texto });
        }
        else if (role.kind === 'style' && role.ref === 'body_paragraph' && ((_a = typedListMarker(p.text)) === null || _a === void 0 ? void 0 : _a.kind) === 'numbered') {
            secao.issues.push({ index: i, kind: 'numero-digitado', text: texto });
        }
        else if (role.kind === 'skip' && role.why === 'capa-antiga') {
            const date = findDateMention(texto);
            secao.issues.push(Object.assign({ index: i, kind: 'capa-antiga', text: texto }, (date ? { date } : {})));
        }
    }
    return secoes.filter((s) => s.issues.length > 0);
}
const ROTULO = {
    'titulo-sem-numero': 'título sem número no texto',
    'titulo-disfarcado': 'linha curta toda em negrito (título disfarçado?)',
    'numero-digitado': 'número digitado à mão no começo',
    'capa-antiga': 'estilo de capa no meio do corpo (folha de rosto antiga?)',
};
/** Os pontos da seção como o prompt da revisão os mostra: um por linha, com índice. */
export function describeIssues(issues) {
    return issues
        .map((it) => `- [${it.index}] ${ROTULO[it.kind]}: «${it.text.length > 70 ? `${it.text.slice(0, 70)}…` : it.text}»` +
        (it.date ? ` (data: «${it.date}» — vai para document_date)` : ''))
        .join('\n');
}
